import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, call } from "../lib/api.ts";
import { supportApi } from "../lib/support.ts";
import { ago, delta, fmtBRLCompact, fmtDay, fmtInt, fmtPct, weekdayName } from "../lib/format.ts";
import { useCan, useCity } from "../lib/session.tsx";
import { Icon } from "../components/Icon.tsx";
import { PageHead } from "../components/Shell.tsx";
import { ErrorBox, Skeleton } from "../components/ui.tsx";
import { typeLabel } from "../lib/trip-types.ts";

type Kpi = { label: string; value: string; unit?: string; change: number | null; changeText?: string; goodWhenUp: boolean };

export function OverviewPage() {
  const { cityId } = useCity();
  const canDrivers = useCan("motoristas");
  const q = useQuery({
    queryKey: ["overview", cityId],
    queryFn: () => call(api.admin.overview.get({ query: { cityId } })),
    refetchInterval: 30_000,
  });
  const d = q.data;
  const lastWeekday = weekdayName(new Date(Date.now() - 7 * 86400_000));

  const kpis: Kpi[] = d
    ? [
        { label: "Corridas concluídas", value: fmtInt(d.kpis.completed.today), unit: "hoje", change: delta(d.kpis.completed.today, d.kpis.completed.lastWeek), goodWhenUp: true },
        { label: "Valor transacionado", value: fmtBRLCompact(d.kpis.gmvCents.today), change: delta(d.kpis.gmvCents.today, d.kpis.gmvCents.lastWeek), goodWhenUp: true },
        { label: "Motoristas online", value: fmtInt(d.kpis.driversOnline.now), unit: "agora", change: delta(d.kpis.driversOnline.now, d.kpis.driversOnline.lastWeek), goodWhenUp: true },
        {
          label: "Cancelamentos",
          value: fmtPct(d.kpis.cancellations.rateToday),
          unit: `${fmtInt(d.kpis.cancellations.today)} hoje`,
          change: d.kpis.cancellations.rateToday !== null && d.kpis.cancellations.rateLastWeek !== null ? d.kpis.cancellations.rateToday - d.kpis.cancellations.rateLastWeek : null,
          changeText: d.kpis.cancellations.rateToday !== null && d.kpis.cancellations.rateLastWeek !== null
            ? `${Math.abs((d.kpis.cancellations.rateToday - d.kpis.cancellations.rateLastWeek) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} p.p.`
            : undefined,
          goodWhenUp: false,
        },
      ]
    : [];

  return (
    <>
      <PageHead icon="grid" title="Visão geral" sub={d ? `· ${fmtDay()} · atualizado ${ago(d.generatedAt)}` : undefined} />
      <ErrorBox error={q.error} />
      <section aria-label="Indicadores" className="grid-auto" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(210px, 100%), 1fr))" }}>
        {!d && [0, 1, 2, 3].map((i) => <div key={i} className="card pad"><Skeleton h={70} /></div>)}
        {kpis.map((k) => <KpiCard key={k.label} k={k} weekday={lastWeekday} />)}
      </section>

      {d && (
        <>
          <section className="row">
            <div className="card pad" style={{ flex: "2 1 520px", minWidth: 0 }}>
              <h2 className="card-title">Corridas concluídas por dia</h2>
              <DailyBars days={d.daily} />
            </div>
            <div className="card pad stack" style={{ flex: "1 1 320px", minWidth: 0 }}>
              <h2 className="card-title">Mix de categorias</h2>
              <Donut items={d.categories} total={d.kpis.completed.today} />
            </div>
          </section>

          <section className="row">
            <div className="card pad stack" style={{ flex: "2 1 520px", minWidth: 0, gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
                <h2 className="card-title">Funil de hoje</h2>
                <span className="muted" style={{ fontSize: 13 }}>Do pedido pago à corrida concluída</span>
              </div>
              <Funnel f={d.funnel} />
            </div>
            <div className="stack" style={{ flex: "1 1 320px", minWidth: 0 }}>
              <div className="card pad stack">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <h2 className="card-title">Aceitação dos motoristas</h2>
                  <span className="muted" style={{ fontSize: 12 }}>meta 90%</span>
                </div>
                <span style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <span className="display num" style={{ fontWeight: 600, fontSize: 34, letterSpacing: "-0.02em" }}>{fmtPct(d.kpis.acceptance.today)}</span>
                  {d.kpis.acceptance.today !== null && (
                    <span style={{ fontSize: 13, fontWeight: 600, color: d.kpis.acceptance.today >= 0.9 ? "var(--ok)" : "var(--warn)" }}>
                      {d.kpis.acceptance.today >= 0.9 ? "acima da meta" : "abaixo da meta"}
                    </span>
                  )}
                </span>
                <span className="bar" style={{ height: 34 }}>
                  <span className="fill" style={{ width: `${(d.kpis.acceptance.today ?? 0) * 100}%`, borderRadius: 10 }} />
                  <span className="rest hatch" style={{ borderRadius: 10 }} />
                </span>
                <span style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }} className="muted">
                  <span>{fmtInt(d.kpis.acceptance.refusedToday)} recusas ou expiradas hoje</span>
                  <span>Semana passada {fmtPct(d.kpis.acceptance.lastWeek)}</span>
                </span>
              </div>
              <div className="card pad stack" style={{ gap: 4 }}>
                <h2 className="card-title" style={{ marginBottom: 6 }}>Precisa de atenção</h2>
                <Attention pendingReviews={canDrivers ? d.attention.pendingReviews : 0} oldestPendingAt={d.attention.oldestPendingAt} />
                <Link to="/ao-vivo/corridas" search={{ id: undefined }} style={{ fontSize: 13, fontWeight: 600, textDecoration: "none" }}>Abrir corridas ao vivo</Link>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}

function KpiCard({ k, weekday }: { k: Kpi; weekday: string }) {
  const up = (k.change ?? 0) >= 0;
  const good = k.change === null ? null : up === k.goodWhenUp || k.change === 0;
  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10, padding: "16px 18px" }}>
      <span className="stat-label" style={{ fontSize: 14 }}><span className="tick" />{k.label}</span>
      <span style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span className="display num" style={{ fontWeight: 600, fontSize: 26, letterSpacing: "-0.02em" }}>{k.value}</span>
        {k.unit && <span className="muted" style={{ fontSize: 13 }}>{k.unit}</span>}
      </span>
      {k.change === null ? (
        <span className="pill tone-neutral" style={{ alignSelf: "flex-start" }}>sem base na {weekday} passada</span>
      ) : (
        <span className={`pill ${good ? "tone-ok" : "tone-bad"}`} style={{ alignSelf: "flex-start" }}>
          <Icon name={up ? "up" : "down"} size={11} stroke={3} />
          {k.changeText ?? fmtPct(Math.abs(k.change))} vs. {weekday} passada
        </span>
      )}
    </div>
  );
}

function DailyBars({ days }: { days: { day: string; completed: number }[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...days.map((d) => d.completed));
  const top = niceMax(max);
  const today = days.length - 1;
  const shown = hover ?? today;
  const label = (iso: string, i: number) => (i === today ? "Hoje" : String(Number(iso.slice(8, 10))));
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", gap: 10 }}>
        <div aria-hidden="true" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 220, fontSize: 11, color: "var(--muted-2)", textAlign: "right", width: 34 }}>
          {[1, 0.75, 0.5, 0.25, 0].map((f) => <span key={f}>{compact(top * f)}</span>)}
        </div>
        <div role="img" aria-label={`Corridas concluídas por dia nas últimas duas semanas; hoje ${fmtInt(days[today]?.completed ?? 0)}`} style={{ flex: 1, height: 220, display: "flex", alignItems: "flex-end", gap: 8 }}>
          {days.map((d, i) => (
            <span key={d.day} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ position: "relative", flex: 1, height: "100%", display: "flex", alignItems: "flex-end" }}>
              {shown === i && (
                <span style={{ position: "absolute", bottom: `calc(${(d.completed / top) * 100}% + 8px)`, left: "50%", transform: "translateX(-50%)", padding: "3px 8px", borderRadius: 8, background: "var(--card)", boxShadow: "0 4px 12px rgba(0,0,0,.15)", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", zIndex: 1 }}>
                  {fmtInt(d.completed)}
                </span>
              )}
              <span className={i === today ? undefined : "hatch"} style={{ width: "100%", height: `${Math.max(1, (d.completed / top) * 100)}%`, borderRadius: 10, backgroundColor: i === today ? "var(--brasa)" : hover === i ? "var(--brasa-tint)" : undefined }} />
            </span>
          ))}
        </div>
      </div>
      <div aria-hidden="true" style={{ display: "flex", gap: 8, margin: "8px 0 0 44px", fontSize: 11, color: "var(--muted-2)" }}>
        {days.map((d, i) => <span key={d.day} style={{ flex: 1, textAlign: "center", color: i === today ? "var(--ink)" : undefined }}>{label(d.day, i)}</span>)}
      </div>
    </div>
  );
}

const niceMax = (v: number) => {
  const mag = 10 ** Math.floor(Math.log10(v));
  return Math.ceil(v / mag / 2) * 2 * mag || 4;
};
const compact = (v: number) => (v >= 1000 ? `${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k` : String(Math.round(v)));

const DONUT_COLORS = ["var(--brasa)", "var(--ink-2)", "var(--donut-3)", "var(--muted-2)"];
function Donut({ items, total }: { items: { category: string; completed: number }[]; total: number }) {
  const C = 2 * Math.PI * 64;
  let acc = 0;
  const sum = items.reduce((a, i) => a + i.completed, 0);
  const segs = items.map((it, i) => {
    const len = sum ? (C * it.completed) / sum : 0;
    const s = { ...it, color: DONUT_COLORS[i % DONUT_COLORS.length]!, dash: `${Math.max(len - 3, 0).toFixed(1)} ${C.toFixed(1)}`, offset: (-acc).toFixed(1), pct: sum ? it.completed / sum : 0 };
    acc += len;
    return s;
  });
  if (!sum) return <div className="empty">Nenhuma corrida concluída hoje.</div>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
      <div role="img" aria-label={segs.map((s) => `${typeLabel(s.category)} ${fmtPct(s.pct)}`).join(", ")} style={{ position: "relative", width: 168, height: 168, flex: "none" }}>
        <svg aria-hidden="true" width="168" height="168" viewBox="0 0 168 168" style={{ transform: "rotate(-90deg)" }}>
          {segs.map((s) => <circle key={s.category} cx="84" cy="84" r="64" fill="none" style={{ stroke: s.color }} strokeWidth="26" strokeDasharray={s.dash} strokeDashoffset={s.offset} />)}
        </svg>
        <span style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <b className="display" style={{ fontSize: 22 }}>{fmtInt(total)}</b>
          <span className="muted" style={{ fontSize: 11 }}>corridas hoje</span>
        </span>
      </div>
      <div style={{ flex: "1 1 140px", display: "flex", flexDirection: "column", gap: 8 }}>
        {segs.map((s) => (
          <span key={s.category} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 10, background: "var(--soft)", fontSize: 13 }}>
            <span className="tick" style={{ background: s.color }} />
            <span style={{ flex: 1 }}>{typeLabel(s.category)}</span>
            <b>{fmtPct(s.pct)}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

function Funnel({ f }: { f: { requested: number; accepted: number; boarded: number; completed: number } }) {
  const steps = [
    { name: "Pedidos", sub: "Corridas pagas", val: f.requested, icon: "search" as const },
    { name: "Aceitos", sub: "Motorista a caminho", val: f.accepted, icon: "check" as const },
    { name: "Embarques", sub: "Código confirmado", val: f.boarded, icon: "arrow" as const },
    { name: "Concluídas", sub: "Corrida finalizada", val: f.completed, icon: "wallet" as const },
  ];
  return (
    <>
      {steps.map((s) => {
        const pct = f.requested ? s.val / f.requested : 0;
        return (
          <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 0", flexWrap: "wrap" }}>
            <span style={{ flex: "none", width: 36, height: 36, borderRadius: 11, background: "var(--brasa-soft)", color: "var(--brasa-text)", display: "grid", placeItems: "center" }}>
              <Icon name={s.icon} />
            </span>
            <span style={{ flex: "0 0 140px", display: "flex", flexDirection: "column" }}>
              <b>{s.name}</b>
              <span className="muted" style={{ fontSize: 12 }}>{s.sub}</span>
            </span>
            <span className="hatch" style={{ flex: "1 1 120px", height: 26, borderRadius: 8, overflow: "hidden", display: "flex" }}>
              <span style={{ width: `${pct * 100}%`, height: "100%", borderRadius: 8, background: "var(--brasa)" }} />
            </span>
            <span style={{ flex: "none", width: 90, textAlign: "right", display: "flex", flexDirection: "column" }}>
              <b className="num" style={{ fontSize: 15 }}>{fmtInt(s.val)}</b>
              <span className="muted" style={{ fontSize: 12 }}>{fmtPct(pct)}</span>
            </span>
          </div>
        );
      })}
    </>
  );
}

/** "Precisa de atenção": urgent support, cadastros waiting, failed or held Pix payouts. */
function Attention({ pendingReviews, oldestPendingAt }: { pendingReviews: number; oldestPendingAt: string | null }) {
  const canSupport = useCan("suporte");
  const canPayouts = useCan("repasses");
  const canSafety = useCan("seguranca");
  const { cityId } = useCity();
  const safety = useQuery({ queryKey: ["safety", cityId, "summary"], queryFn: () => call(api.admin.safety.summary.get({ query: { cityId } })), enabled: canSafety && !!cityId, refetchInterval: 30_000 });
  const support = useQuery({ queryKey: ["support", "badge"], queryFn: () => supportApi.list({ status: "waiting_staff" }), enabled: canSupport, refetchInterval: 30_000, retry: false });
  const payouts = useQuery({ queryKey: ["payouts", "attention"], queryFn: () => call(api.admin.payouts.attention.get()), enabled: canPayouts, refetchInterval: 60_000 });
  const c = support.data?.counts;
  const waiting = c ? c.waiting_staff + c.open : 0;
  const rows: { tone: string; label: string; title: string; sub: string; to: "/suporte" | "/cadastros" | "/repasses" | "/seguranca"; search: never }[] = [];
  const openAlerts = safety.data?.openAlerts ?? 0;
  if (openAlerts > 0) rows.push({ tone: "tone-bad", label: "Segurança", title: `${openAlerts} ${openAlerts === 1 ? "corrida fora da rota" : "corridas fora da rota"}`, sub: "Desvio de rota com a corrida ainda em andamento", to: "/seguranca", search: { tipo: "open", periodo: "today" } as never });
  if (c && c.urgent > 0) rows.push({ tone: "tone-bad", label: "Crítico", title: `${c.urgent} ${c.urgent === 1 ? "atendimento urgente" : "atendimentos urgentes"}`, sub: "Central de segurança e incidentes", to: "/suporte", search: { status: "active", app: "all" } as never });
  if (pendingReviews > 0) rows.push({ tone: "tone-warn", label: "Atenção", title: `${pendingReviews} cadastros aguardando`, sub: `Mais antigo enviado ${ago(oldestPendingAt)}`, to: "/cadastros", search: { fila: "pending", id: undefined } as never });
  if (payouts.data && payouts.data.failed > 0) rows.push({ tone: "tone-warn", label: "Atenção", title: `${payouts.data.failed} ${payouts.data.failed === 1 ? "repasse Pix falhou" : "repasses Pix falharam"}`, sub: "Reprocessar depois de conferir a chave", to: "/repasses", search: { filtro: "failed" } as never });
  if (waiting > 0) rows.push({ tone: "tone-info", label: "Suporte", title: `${waiting} ${waiting === 1 ? "pessoa aguardando" : "pessoas aguardando"} resposta`, sub: "Atendimentos sem resposta da equipe", to: "/suporte", search: { status: "waiting_staff", app: "all" } as never });
  if (payouts.data && payouts.data.held > 0) rows.push({ tone: "tone-neutral", label: "Info", title: `${payouts.data.held} ${payouts.data.held === 1 ? "repasse retido" : "repasses retidos"}`, sub: "Revisar antes do próximo lote", to: "/repasses", search: { filtro: "held" } as never });
  if (!rows.length) return <span className="muted" style={{ padding: "9px 0" }}>Nada pendente agora.</span>;
  return (
    <>
      {rows.map((r) => (
        <Link key={r.title} to={r.to} search={r.search} style={{ display: "flex", gap: 12, alignItems: "center", padding: "9px 0", color: "var(--ink)", textDecoration: "none" }}>
          <span className={`pill ${r.tone} pill-dot`} style={{ width: 86, flex: "none" }}>{r.label}</span>
          <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <b>{r.title}</b>
            <span className="muted" style={{ fontSize: 12 }}>{r.sub}</span>
          </span>
        </Link>
      ))}
    </>
  );
}
